import * as React from 'react'
import { useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'

import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core'
import type {
  DragEndEvent,
  DragStartEvent,
  CollisionDetection,
  DragOverEvent,
  DragCancelEvent,
} from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { isPast } from 'date-fns'

import { useIsSplitEnabled } from '@buffer-mono/features'
import {
  Button,
  CalendarClockIcon,
  EmptyState,
  mergeRefs,
  PlusIcon,
  toast,
  useDelayedLoading,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { DateSeparator } from '~publish/components/DateSeparator'
import { NewPostComposerTrigger } from '~publish/components/NewPostComposerTrigger'
import { PostCard, PostCard_Post } from '~publish/components/PostCard'
import { PostingGoalSlotsTour } from '~publish/components/PostingGoalSlotsTour'
import { PostingTimeline } from '~publish/components/PostingTimeline'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { maskFragments, readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import type { PostCard_PostData } from '~publish/components/PostCard'
import { DROP_ANIMATION } from '~publish/helpers/dndkit/constants'
import {
  isPausedPost,
  postStatusByTab,
  isScheduledPost,
} from '~publish/helpers/post'
import type { FilterablePostType } from '~publish/helpers/postType'
import { useCustomSensors } from '~publish/hooks/useCustomSensors'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import { useInfiniteScrollPagination } from '~publish/hooks/useInfiniteScrollPagination'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { usePostDraggedTracking } from '~publish/hooks/usePostDraggedTracking'
import { useSlotsEnabled } from '~publish/hooks/useSlotsEnabled'
import { getRecurrenceSummary } from '~publish/components/PostCard/PostCardActions/recurrenceHelpers'
import { useTimezone } from '~publish/hooks/useTimezone'
import {
  PostListError,
  PostListSkeleton,
} from '~publish/pages/AllChannels/PostList'
import {
  POSTS_PER_PAGE,
  usePaginatedPostList,
} from '~publish/pages/AllChannels/PostList/usePaginatedPostList'
import { canPerform } from '~publish/utils/permissions/canPerform'

import { DraggablePostCard } from './DraggablePostCard'
import { QueueList_Channel } from './fragments/QueueList_Channel'
import { generatePostingSlots, mergePostsAndSlots } from './postSlotManager'
import type { Slot } from './postSlotManager'
import { QueueWelcomeBanner } from './QueueWelcomeBanner'
import { SlotOrPostCard } from './SlotOrPostCard'
import { useDropPostMutation } from './useDropPostMutation'
import { useSwapPostsMutation } from './useSwapPostsMutation'

import styles from './QueueList.module.css'

export { QueueList_Channel }

const EMPTY_POST_TYPES: readonly FilterablePostType[] = []

const isStringIdentifier = (value: unknown): value is string =>
  typeof value === 'string'

const isPost = (item: unknown): item is PostCard_PostData => {
  return (
    typeof item === 'object' &&
    item !== null &&
    '__typename' in item &&
    item.__typename === 'Post'
  )
}

type QueueListProps = {
  channel: FragmentOf<typeof QueueList_Channel>
  tagIds?: string[]
  postTypes?: readonly FilterablePostType[]
}

export const QueueList = React.forwardRef<HTMLDivElement, QueueListProps>(
  ({ channel: passedChannel, tagIds, postTypes }, ref): JSX.Element => {
    const timezone = useTimezone()
    const organizationId = useOrganizationId()
    const isRecurringEnabled = useIsSplitEnabled('publish-recurring-posts')
    const channel = readFragment(QueueList_Channel, passedChannel)

    const isDragAndDropEnabled =
      !channel.isQueuePaused && canPerform(channel, 'managePostingSchedule')

    const {
      data,
      loading,
      error,
      lastElementRef: lastPostRef,
      fetchingMore,
    } = usePaginatedPostList({
      status: postStatusByTab.queue,
      channelIds: [channel.id],
      // Note - we do NOT filter out posts by tagIds or postTypes, as we need
      // to know if a post slot is occupied. Filtering is done in the
      // SlotOrPostCard component by visually hiding the post.
      tagIds: [],
      noteCounts: 'notesButton',
    })

    // The skeleton reveals after the CSS delay. The hook holds it mounted for
    // a minimum duration, so a response just after the reveal never cuts the
    // fade short. Cached posts render at once: the query is cache-and-network,
    // so `loading` alone would put a skeleton over them.
    const emptyWhileLoading = loading && !data?.posts?.edges?.length
    const heldLoading = useDelayedLoading(emptyWhileLoading)
    const showSkeleton = emptyWhileLoading || heldLoading

    const slotNewDesignEnabled = useIsSplitEnabled(
      'geid-set-my-posting-schedule',
    )

    const flattenedPostEdges =
      data?.posts?.edges?.map((edge) => edge.node) ?? []
    const posts = readFragment(PostCard_Post, flattenedPostEdges)
    const [numberOfSlots, setNumberSlots] =
      React.useState<number>(POSTS_PER_PAGE)
    const [lastSlotRef] = useInfiniteScrollPagination({
      loading: false,
      hasNextPage: true,
      fetchMore: async () => setNumberSlots((prev) => prev + POSTS_PER_PAGE),
      rootMargin: '500px',
    })
    const [activeDraggablePostId, setActiveDraggablePostId] = useState<
      string | null
    >(null)
    const innerFeedRef = useRef<HTMLDivElement>(null)
    const feedRef = mergeRefs(innerFeedRef, ref)
    const [dropPostInQueue] = useDropPostMutation()
    const [swapPostsInQueue] = useSwapPostsMutation()
    const dateTimeFormatter = useDateTimeFormatter()
    const sensors = useCustomSensors()
    const trackPostDrag = usePostDraggedTracking()
    const openGlobalModal = useOpenGlobalModal()
    const [showTimeSlotsPreference] = useSlotsEnabled()
    // `canCreatePosts` gates the welcome banner too: it pitches setting a
    // posting schedule, which a non-posting actor can't do.
    const canCreatePosts = canPerform(channel, 'manageUpdates')
    const showTimeSlots = showTimeSlotsPreference && canCreatePosts

    const clickSetPostingGoal = (): void => {
      if (!channel) return
      openGlobalModal(MODALS.postingGoalConfiguration, {
        channelId: channel.id,
        source: 'channel-page',
      })
    }

    const isFilterActive = !!tagIds?.length || !!postTypes?.length
    const isPostingScheduleEmpty =
      !channel.postingSchedule?.length ||
      channel.postingSchedule.every((slot) => !slot.times.length || slot.paused)
    const hasPosts = Boolean(posts?.length)
    const hasVisibleTimeSlots = !isPostingScheduleEmpty && showTimeSlots
    const shouldShowEmptyState =
      !hasPosts && !hasVisibleTimeSlots && !isFilterActive

    const pausedPosts = posts.filter(isPausedPost)
    const scheduledPosts = posts.filter(isScheduledPost)

    const hasNextPage = data?.posts.pageInfo.hasNextPage
    const lastPostDueAt = scheduledPosts?.[scheduledPosts.length - 1]?.dueAt
    const scheduleSlots = generatePostingSlots({
      postingSchedule: channel.postingSchedule,
      timezone,
      endDate: lastPostDueAt,
      hasNextPage,
      numberOfSlots,
    })

    const showPostingGoalSlotsTour = !slotNewDesignEnabled

    const postsAndSlots = mergePostsAndSlots({
      posts: scheduledPosts,
      slots: showTimeSlots ? scheduleSlots : [],
      callback: () => {
        if (showTimeSlots && scheduleSlots.length > numberOfSlots) {
          setNumberSlots(scheduleSlots.length)
        }
      },
    })

    const firstSlot = useMemo(
      () => postsAndSlots.find((item): item is Slot => !isPost(item)),
      [postsAndSlots],
    )

    const announcements = {
      onDragStart({ active }: DragStartEvent): string {
        if (!active.data.current?.dueAt) return ``
        return `Picked up post scheduled for ${dateTimeFormatter(
          active.data.current.dueAt,
        )}.`
      },
      onDragOver({ active, over }: DragOverEvent): string {
        if (!active.data.current?.dueAt || !over?.id || over.id === active.id)
          return ``

        if (over) {
          if (over.data.current?.type === 'slot') {
            const slotDate = over.data.current?.date
            if (!isStringIdentifier(slotDate)) return ''
            return `Post scheduled for ${dateTimeFormatter(
              active.data.current.dueAt,
            )} is over empty slot at ${dateTimeFormatter(slotDate)}.`
          } else {
            if (over.data.current?.type === 'post') {
              return `Post scheduled for ${dateTimeFormatter(
                active.data.current.dueAt,
              )}  is over another post scheduled for ${dateTimeFormatter(
                over.data.current.dueAt,
              )}. Dropping will swap the posts.`
            }
          }
        }
        return `Post is no longer over a droppable area.`
      },
      onDragEnd({ active, over }: DragEndEvent): string {
        if (!active.data.current?.dueAt) return ``
        if (over?.id === active.id)
          return `Post scheduled for ${dateTimeFormatter(
            active.data.current.dueAt,
          )} was moved to its original position.`

        if (over) {
          if (over.data.current?.type === 'slot') {
            if (!isStringIdentifier(over.id)) return ''
            return `Post scheduled for ${dateTimeFormatter(
              active.data.current.dueAt,
            )} was rescheduled to ${dateTimeFormatter(over.id)}.`
          } else {
            if (over.data.current?.type === 'post') {
              return `Post scheduled for ${dateTimeFormatter(
                active.data.current.dueAt,
              )} was swapped with post scheduled for ${dateTimeFormatter(
                active.data.current.dueAt,
              )}`
            }
          }
        }
        return `Post was dropped back in its original position.`
      },
      onDragCancel({ active }: DragCancelEvent): string {
        if (!active.data.current?.dueAt) return ``

        return `Dragging was cancelled. Post scheduled for ${dateTimeFormatter(
          active.data.current.dueAt,
        )} was returned to its original position.`
      },
    }

    const activeDraggablePost = activeDraggablePostId
      ? posts.find((post) => post.id === activeDraggablePostId)
      : undefined

    const handleDragStart = (event: DragStartEvent): void => {
      if (!isStringIdentifier(event.active.id)) return
      setActiveDraggablePostId(event.active.id)
      const post = posts.find((p) => p.id === event.active.id)
      if (post) trackPostDrag(post)
    }

    const handleDragEnd = async (event: DragEndEvent): Promise<void> => {
      const { active, over } = event

      setActiveDraggablePostId(null)
      const isSlotDrop = over?.data?.current?.type === 'slot'
      if (
        over &&
        active.id !== over.id &&
        isSlotDrop &&
        isStringIdentifier(active.id) &&
        isStringIdentifier(over.id)
      ) {
        const draggedPostId = active.id
        const newSlotDate = over.id

        const dropPostResult = await dropPostInQueue({
          variables: { input: { id: draggedPostId, dueAt: newSlotDate } },
        })

        if (dropPostResult.success) {
          toast.success('Post rescheduled successfully')
        } else {
          toast.error(
            `Failed to reschedule post, ${dropPostResult.error.message}`,
          )
        }
      }

      if (
        over &&
        active.id !== over.id &&
        !isSlotDrop &&
        isStringIdentifier(active.id) &&
        isStringIdentifier(over.id)
      ) {
        const draggedPostId = active.id
        const overPostId = over.id

        const swapPostsResult = await swapPostsInQueue({
          variables: {
            input: { sourceId: draggedPostId, targetId: overPostId },
          },
        })

        if (swapPostsResult.success) {
          toast.success('Posts swapped successfully')
        } else {
          toast.error(swapPostsResult.error.message)
        }
      }
    }

    if (showSkeleton) {
      return <PostListSkeleton />
    }

    if (error && !data) {
      return <PostListError error={error} />
    }

    // no posts in general
    if (shouldShowEmptyState) {
      return (
        <>
          {canCreatePosts && <QueueWelcomeBanner />}
          <EmptyState size="large" variant="primary" ref={ref}>
            <EmptyState.Icon>
              <CalendarClockIcon />
            </EmptyState.Icon>
            <EmptyState.Heading>No posts scheduled</EmptyState.Heading>
            <EmptyState.Description>
              {canCreatePosts
                ? 'Schedule some posts and they will appear here'
                : 'When posts are scheduled, they will appear here'}
            </EmptyState.Description>
            {canCreatePosts && (
              <EmptyState.Actions>
                {channel.postingGoal && isPostingScheduleEmpty && (
                  <Button
                    size="large"
                    variant="secondary"
                    as={Link}
                    to={`/channels/${channel.id}/settings?tab=posting-schedule`}
                  >
                    Set Posting Schedule
                  </Button>
                )}
                {!channel.postingGoal && (
                  <Button
                    size="large"
                    variant="secondary"
                    onClick={clickSetPostingGoal}
                  >
                    Set a Posting Goal
                  </Button>
                )}
                <NewPostComposerTrigger
                  cta="publish-queue-emptyState-newPost-1"
                  channels={[channel.id]}
                >
                  <Button size="large">
                    <PlusIcon /> New Post
                  </Button>
                </NewPostComposerTrigger>
              </EmptyState.Actions>
            )}
          </EmptyState>
        </>
      )
    }

    return (
      <>
        {canCreatePosts && <QueueWelcomeBanner />}
        {showPostingGoalSlotsTour && <PostingGoalSlotsTour />}
        <DndContext
          autoScroll={true}
          onDragEnd={handleDragEnd}
          onDragStart={handleDragStart}
          collisionDetection={collisionDetectionAlgorithm}
          sensors={sensors}
          modifiers={[restrictToFirstScrollableAncestor]}
          accessibility={{
            announcements,
          }}
        >
          <PostingTimeline
            role="feed"
            aria-busy={fetchingMore}
            data-testid="queue-list"
            ref={feedRef}
          >
            {(pausedPosts.length > 0 || postsAndSlots.length > 0) && (
              <VisuallyHidden as="h2">Queue</VisuallyHidden>
            )}
            {pausedPosts.length > 0 && (
              <>
                <PostingTimeline.Header as="h3">
                  Not Published
                </PostingTimeline.Header>
                {pausedPosts.map((post) => {
                  const summary =
                    post.status === 'scheduled'
                      ? getRecurrenceSummary(
                          post.recurrence,
                          post.dueAt,
                          isRecurringEnabled,
                          timezone,
                        )
                      : null
                  return (
                    <PostingTimeline.Entry key={post.id}>
                      <PostingTimeline.TimeLabel
                        date={post.dueAt}
                        status={post.status}
                        notificationStatus={post.notificationStatus}
                        schedulingType={post.schedulingType}
                        includeDate
                        customScheduled={post.isCustomScheduled}
                        isPinned={post.isPinned}
                        overdue={isPast(post.dueAt) && post.error === null}
                        recurring={!!summary}
                        recurrenceSummary={summary}
                      />
                      <PostingTimeline.Content>
                        {
                          // Leaving the "DraggablePostCard" for now as we'll probably
                          // want to enable drag and drop for paused posts in the future
                          isDragAndDropEnabled &&
                          post.allowedActions.includes('updatePost') ? (
                            <DraggablePostCard
                              post={maskFragments([PostCard_Post], post)}
                            />
                          ) : (
                            <PostCard
                              post={maskFragments([PostCard_Post], post)}
                              rounded="lg"
                            />
                          )
                        }
                      </PostingTimeline.Content>
                    </PostingTimeline.Entry>
                  )
                })}
              </>
            )}
            {postsAndSlots.map((item, index, list) => {
              const currentDate = isPost(item) ? item.dueAt : item.date
              const prevItem = index > 0 ? list[index - 1] : undefined
              const prevDate = isPost(prevItem)
                ? prevItem?.dueAt
                : prevItem?.date
              const isFirstSlot = !isPost(item) && item.date === firstSlot?.date

              const extraProps = isFirstSlot ? { 'data-tour-id': 'slot' } : {}

              return (
                <React.Fragment key={isPost(item) ? item.id : item.date}>
                  <DateSeparator
                    currentDate={currentDate}
                    previousDate={prevDate}
                  />
                  <SlotOrPostCard
                    item={item}
                    tagIds={tagIds ?? []}
                    postTypes={postTypes ?? EMPTY_POST_TYPES}
                    channelId={channel.id}
                    channelService={!hasPosts ? channel.service : undefined}
                    organizationId={organizationId ?? undefined}
                    isDragAndDropEnabled={isDragAndDropEnabled}
                    {...extraProps}
                  />
                </React.Fragment>
              )
            })}

            {(hasNextPage || showTimeSlots) && (
              <PostingTimeline.Loading
                ref={hasNextPage ? lastPostRef : lastSlotRef}
              />
            )}
          </PostingTimeline>
          {createPortal(
            <DragOverlay
              dropAnimation={DROP_ANIMATION}
              className={styles.dragOverlay}
            >
              {activeDraggablePost && (
                <DraggablePostCard
                  post={maskFragments([PostCard_Post], activeDraggablePost)}
                  inOverlay={true}
                />
              )}
            </DragOverlay>,
            document.body,
          )}
        </DndContext>
      </>
    )
  },
)

QueueList.displayName = 'QueueList'

const collisionDetectionAlgorithm: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args)

  if (pointerCollisions.length > 0) {
    return pointerCollisions
  }

  return rectIntersection(args)
}
